/**
 * Beacon, presentation v1.0 — Canvas 04 §01's field: navy sky, faint stars, one
 * beacon ring that lights. Points are shown during the run and are feedback,
 * not the measurement; the four measures are worked out after the run. No
 * sound. The coat never changes a foreperiod, a window or a score: it only
 * hears about them.
 */
import { BEACON_PRESENTATION, beaconPoints } from '../protocol'
import type { Summary, TrialRecord } from '../runner'
import { fmtClock, type Presentation } from './types'

export class BeaconPresentation implements Presentation {
  readonly version = BEACON_PRESENTATION
  private root: HTMLElement | null = null
  private beacon: HTMLElement | null = null
  private points = 0

  mount(root: HTMLElement): void {
    this.root = root
    this.points = 0
    // Add, never replace: the host sizes the root with its own class.
    root.classList.remove('field--plain', 'field--beacon', 'field--paused')
    root.classList.add('field', 'field--beacon')
    const stars = Array.from({ length: 40 }, (_, i) => {
      // Deterministic star field: decoration with a fixed layout, so two runs look the same.
      const x = (i * 37) % 100
      const y = (i * 53) % 100
      return `<i class="star" style="left:${x}%;top:${y}%;opacity:${0.2 + ((i * 7) % 5) / 10}"></i>`
    }).join('')
    root.innerHTML = `
      ${stars}
      <div class="field__hud"><span>BEACON</span><span data-signal>signal —</span><span data-clock>3:00</span></div>
      <div class="beacon" data-beacon><div class="beacon__core"></div></div>
      <div class="field__msg" data-msg>Respond with <span class="key">space</span> or a tap when the beacon lights</div>
      <div class="field__foot"><span>points are feedback, not the measurement</span><span data-points>0 pts</span></div>`
    this.beacon = root.querySelector('[data-beacon]')
  }
  onArmed(): void {
    this.beacon?.classList.remove('beacon--lit')
    const msg = this.root?.querySelector('[data-msg]')
    if (msg) msg.textContent = 'wait for the light'
  }
  onLit(): void {
    this.beacon?.classList.add('beacon--lit')
    const msg = this.root?.querySelector('[data-msg]')
    if (msg) msg.textContent = 'now'
  }
  onTrialEnd(trial: TrialRecord): void {
    this.beacon?.classList.remove('beacon--lit')
    this.points += beaconPoints(trial.rtMs, trial.outcome ?? '')
    const pts = this.root?.querySelector('[data-points]')
    if (pts) pts.textContent = `${this.points.toLocaleString()} pts`
    const msg = this.root?.querySelector('[data-msg]')
    if (msg && trial.outcome === 'false_start') msg.textContent = 'too early — wait for the light'
  }
  onTick(elapsedMs: number, durationMs: number, signalIndex: number): void {
    const clock = this.root?.querySelector('[data-clock]')
    if (clock) clock.textContent = `${fmtClock(durationMs - elapsedMs)} left`
    const sig = this.root?.querySelector('[data-signal]')
    if (sig) sig.textContent = `signal ${signalIndex}`
  }
  onEnded(_summary: Summary): void {
    const msg = this.root?.querySelector('[data-msg]')
    if (msg) msg.textContent = 'Done. Points were feedback; the four measures are worked out after the run.'
  }
  onInterrupted(reason: string, elapsedMs: number): void {
    this.root?.classList.add('field--paused')
    const veil = document.createElement('div')
    veil.className = 'field__veil'
    veil.innerHTML = `<strong>${reason === 'blur' ? 'The window lost focus' : reason === 'hidden' ? 'The tab was hidden' : reason === 'stale_event' ? 'An input arrived late' : 'Stopped'} at ${fmtClock(elapsedMs)}</strong>
      <span>This attempt stops here and is kept as interrupted. Trials are never stitched around an unrecorded break, so the next try is a fresh attempt.</span>`
    this.root?.appendChild(veil)
  }
  unmount(): void {
    if (this.root) this.root.innerHTML = ''
  }
}
